/**
 * 「代行して開く」の導線(オペレーター向け)。
 *
 * ## 理由は必須
 *
 * 閉じた語彙から 1 つ選ばないと押せない。自由記述は任意で、監査にだけ載る。
 * **押してから断られる形にしない**ので、選ぶまでボタンを押せなくしてある
 * (判断の正本はサーバー側で、ここは押す前に分かるようにするためのもの)。
 *
 * ## 窓は押した瞬間に開く
 *
 * 発行を待ってから `window.open` すると、ポップアップブロッカーに止められる。
 * 先に空の窓(受け取り口の画面)を開き、トークンが出てからその窓へ渡す。
 * 発行に失敗したら窓を閉じる。
 *
 * ## トークンを画面に出さない
 *
 * 受け取ったトークンは変数のまま `postMessage` に渡すだけで、state にも
 * URL にも入れない。渡したら参照を捨てる。
 */

import { useState } from "react";
import { useMutation } from "urql";
import { Button } from "visumo-data-agent-web-shared";
import {
  DELEGATION_HANDOFF_PATH,
  DELEGATION_HANDOFF_TIMEOUT_MS,
  DELEGATION_HANDOFF_TOKEN,
  isDelegationHandoffReady,
} from "visumo-data-agent-web-shared";

import { StartDelegationDocument } from "./graphql/generated/graphql";

/** 設計の閉じた語彙。ここが正本ではないので、増やすときは SDL と一緒に直す。 */
const REASONS = [
  { value: "SETUP_SUPPORT", label: "初期設定の支援" },
  { value: "TROUBLESHOOTING", label: "不具合の調査" },
  { value: "CUSTOMER_REQUEST", label: "事業者からの依頼" },
  { value: "PERIODIC_REVIEW", label: "定例の確認" },
] as const;

type Reason = (typeof REASONS)[number]["value"];

/** 代行で開く先。未設定なら導線そのものを出さない。 */
export function tenantAppOrigin(): string {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return env["VITE_ADMIN_TENANT_APP_ORIGIN"] ?? "";
}

export interface DelegateOpenButtonProps {
  readonly tenantId: string;
  readonly tenantLabel: string;
}

export function DelegateOpenButton(props: DelegateOpenButtonProps) {
  const origin = tenantAppOrigin();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<Reason | "">("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, startDelegation] = useMutation(StartDelegationDocument);

  if (origin === "") {
    // 繋がっていない口を押せる形にしない
    return null;
  }

  const handoff = (target: Window, accessToken: string, expiresIn: number) => {
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== origin) return;
      if (!isDelegationHandoffReady(event.data)) return;
      target.postMessage(
        { type: DELEGATION_HANDOFF_TOKEN, accessToken, expiresIn },
        origin,
      );
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
    };
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      setError("代行の窓が開けませんでした。もう一度お試しください。");
    }, DELEGATION_HANDOFF_TIMEOUT_MS);
    window.addEventListener("message", onMessage);
  };

  const submit = (): void => {
    if (reason === "") return;
    setError(null);
    // 押した瞬間に開く(発行を待つとブロッカーに止められる)
    const target = window.open(`${origin}${DELEGATION_HANDOFF_PATH}`, "_blank");
    if (target === null) {
      setError(
        "ブラウザが新しい窓を止めました。このサイトのポップアップを許可してください。",
      );
      return;
    }
    void startDelegation({
      input: {
        tenantId: props.tenantId,
        reason,
        ...(note.trim() === "" ? {} : { reasonNote: note.trim() }),
      },
    }).then((outcome) => {
      const payload = outcome.data?.operator.startDelegation;
      const userError = payload?.userErrors[0];
      if (outcome.error !== undefined || userError !== undefined) {
        target.close();
        setError(userError?.message ?? "代行を開始できませんでした。");
        return;
      }
      const accessToken = payload?.accessToken;
      const expiresIn = payload?.expiresIn;
      if (accessToken == null || accessToken === "" || expiresIn == null) {
        target.close();
        setError("代行を開始できませんでした。");
        return;
      }
      handoff(target, accessToken, expiresIn);
      setOpen(false);
      setReason("");
      setNote("");
    });
  };

  if (!open) {
    return (
      <Button
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        代行して開く
      </Button>
    );
  }

  return (
    <div className="vd-stack" data-testid="delegate-form">
      <p className="vd-stat-note" style={{ margin: 0 }}>
        {props.tenantLabel}の画面を代行で開きます。理由は監査に残り、代行は 60
        分で終わります。
      </p>
      <label>
        <span style={{ marginRight: 6 }}>理由（必須）</span>
        <select
          aria-label="理由（必須）"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value as Reason | "");
          }}
        >
          <option value="">選んでください</option>
          {REASONS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span style={{ marginRight: 6 }}>補足（任意）</span>
        <input
          aria-label="補足（任意）"
          type="text"
          value={note}
          maxLength={500}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </label>
      {error === null ? null : (
        <p className="vd-stat-note" role="alert">
          {error}
        </p>
      )}
      <div>
        <Button disabled={reason === "" || result.fetching} onClick={submit}>
          代行を開始する
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          やめる
        </Button>
      </div>
    </div>
  );
}
